import type { IPO } from '@/lib/api-client';

/** Today as YYYY-MM-DD in the given IANA timezone. en-CA renders ISO order. */
export function todayYmdInTz(tz?: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz }).format(now);
}

/** Subtract whole months from a YYYY-MM-DD string, returning YYYY-MM-DD.
 *  UTC-midnight arithmetic — DST-safe since the result is read as a date only. */
export function ymdMinusMonths(ymd: string, months: number): string {
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  const dt = new Date(Date.UTC(y, (m - 1) - months, d));
  return dt.toISOString().slice(0, 10);
}

/** How far back a `Filed` IPO's filing date can be and still count as an
 *  active part of the upcoming pipeline. Older filings are almost always
 *  abandoned (the deal never priced) and would just be stale clutter. */
export const FILED_LOOKBACK_MONTHS = 9;

/**
 * "Upcoming" = in the IPO pipeline, not yet priced.
 *
 * The subtlety: `ipoDate` means different things by status. For `Scheduled`
 * it's the expected pricing date (future). For `Filed` it's the S-1 FILING
 * date — always in the past, because a company files before it prices. The
 * old `ipoDate >= today` rule therefore dropped EVERY filed deal (the bulk of
 * the real pipeline), leaving only the handful of already-dated `Scheduled`
 * rows. We now keep recently-filed deals (filing within the last
 * FILED_LOOKBACK_MONTHS) and still require `Scheduled`/`Postponed` to be
 * today-or-later. `Priced`/`Withdrawn` are never "upcoming".
 */
export function isUpcomingIpo(
  ipo: Pick<IPO, 'status' | 'ipoDate'>,
  todayYmd: string,
  filedLookbackMonths: number = FILED_LOOKBACK_MONTHS,
): boolean {
  if (ipo.status === 'Priced' || ipo.status === 'Withdrawn') return false;
  const date = (ipo.ipoDate ?? '').slice(0, 10);
  if (!date) return false; // no date at all — can't confirm it's in the pipeline
  if (ipo.status === 'Filed') {
    // Filed deals carry a past filing date by design — keep them if filed
    // recently, drop ancient (likely dead) filings.
    return date >= ymdMinusMonths(todayYmd, filedLookbackMonths);
  }
  // Scheduled / Postponed: dated with an expected pricing date — today or later.
  return date >= todayYmd;
}

/** Stable ordering for the upcoming list: dated deals (Scheduled/Postponed)
 *  first, soonest expected date leading; then Filed deals by most-recent
 *  filing (their date column renders as "TBD" since it's only a filing date). */
export function compareUpcoming(
  a: Pick<IPO, 'status' | 'ipoDate'>,
  b: Pick<IPO, 'status' | 'ipoDate'>,
): number {
  const aFiled = a.status === 'Filed';
  const bFiled = b.status === 'Filed';
  if (aFiled !== bFiled) return aFiled ? 1 : -1;
  const ad = (a.ipoDate ?? '').slice(0, 10);
  const bd = (b.ipoDate ?? '').slice(0, 10);
  // Filed: newest filing first (desc). Dated: soonest first (asc).
  return aFiled ? bd.localeCompare(ad) : ad.localeCompare(bd);
}
